import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Application, Container, Graphics, Point, Sprite, Texture } from 'pixi.js';
import { Util } from 'src/util/util';
import { Account } from 'src/sync/account';
import { Room } from 'src/sync/room';
import { IRoomAsset } from 'src/sync/_api';
import { View } from 'src/graphics/view';
import { Store } from 'src/sync/store';
import { Gfx } from 'src/graphics/gfx';
import { Viewport } from 'lib/viewport';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    templateUrl: './tokenizer.component.html',
    styleUrls: ['./tokenizer.component.scss'],
    standalone: false
})
export class TokenizerComponent {
    @ViewChild('uploadDisplay') uploadDisplay!: ElementRef<Node>;
    private backgroundColor = 0x333333;
    private background!: Graphics;
    private radius = 250;
    private center = this.radius * 1.2;
    private ringWidth = 26;
    private isRotating = false;
    private rotationOffset = 0;
    public viewport!: Viewport;
    public app!: Application;
    public container!: Container;
    public token!: Sprite;
    public mask!: Graphics;
    public color = Util.getRandomHexcolor();
    public alpha = 1;
    public ring = new Sprite(Gfx.textures['metal-ring']);
    public canEyedrop = (window as any).EyeDropper;
    // TODO: This list being duplicated with the list in gfx.ts is bad
    public ringTextures = [
        { source: 'metal-ring', ringWidth: 26 },
        { source: 'rope-ring', ringWidth: 13 },
        { source: 'harsh-ring', ringWidth: 26 },
        { source: 'iron-ring', ringWidth: 35 },
        { source: 'thin-ring', ringWidth: 4 },
        { source: 'segment-ring', ringWidth: 36 },
        { source: 'star-ring', ringWidth: 30 },
        { source: 'flame-ring', ringWidth: 16 },
        { source: 'leaf-ring', ringWidth: 55 },
        { source: 'cyber-ring', ringWidth: 20 },
        { source: 'aztec-ring', ringWidth: 55 }
    ];

    public eyedrop() {
        const eyeDropper = new (window as any).EyeDropper();
        eyeDropper.open().then((x: any) => this.color = x.sRGBHex);
    }

    constructor(
        public snackbar: MatSnackBar,
        public dialogRef: MatDialogRef<TokenizerComponent>,
        protected cdr: ChangeDetectorRef
    ) { }

    public async ngOnInit() {
        this.app = new Application();
        await this.app.init({ width: this.center * 2, height: this.center * 2 });
        Util.suppressWarnings(() => {
            this.container = this.app.stage.addChild(new Container());
            this.background = this.container.addChild(new Graphics());
            this.background.circle(this.center, this.center, this.radius).fill(this.backgroundColor);
            this.viewport = this.container.addChild(new Viewport({
                screenWidth: this.app.renderer.width,
                screenHeight: this.app.renderer.height,
                events: this.app.renderer.events
            }));

            this.viewport.wheel({ smooth: 3, percent: -0.8, center: this.viewport.center });
            this.viewport.drag({ mouseButtons: 'left' });
            this.viewport.options.disableOnContextMenu = true;
            this.viewport.on('moved', (e) => {
                if (e.type != 'wheel') {
                    this.viewport.wheel({ smooth: 3, percent: -0.8, center: this.viewport.center });
                }
            });
            // HACK: Prevent stupid non-referees from uploading tokens that already have borders
            if (Store.isReferee.value == false) {
                this.viewport.zoom(-50, true);
            }
        });

        View.viewport.interactive = false;
        this.uploadDisplay.nativeElement.appendChild(this.app.canvas);
        const texture = Texture.from(Store.imageUpload);
        this.token = this.viewport.addChild(new Sprite(texture));

        const rescale = this.token.width > this.token.height
            ? (this.radius * 2) / this.token.height
            : (this.radius * 2) / this.token.width;

        this.token.anchor.set(0.5);
        this.token.height *= rescale;
        this.token.width *= rescale;
        this.token.x = this.center;
        this.token.y = this.center;

        this.mask = this.container.addChild(new Graphics());
        this.mask.rect(0, 0, this.center * 2, this.center * 2).fill({ color: 0xcccccc, alpha: 0.5 }).circle(this.center, this.center, this.radius).cut();

        const ring = new Sprite(Gfx.textures['metal-ring']);
        ring.width = (this.radius + this.ringWidth) * 2;
        ring.height = (this.radius + this.ringWidth) * 2;
        ring.position.set(this.center, this.center);
        ring.anchor.set(0.5);
        ring.tint = this.color;
        this.ring = this.container.addChild(ring);

        this.cdr.detectChanges();
    }

    public updateColor() {
        if (this.token == null) return;
        this.token.alpha = this.alpha;
        this.ring.tint = this.color;
        this.cdr.detectChanges();
    }

    public setTexture(spec?: { source: string, ringWidth: number }) {
        if (spec === undefined) {
            spec = { source: 'null-ring', ringWidth: 0 }
        }

        this.ringWidth = spec.ringWidth;
        this.ring.texture = Gfx.textures[spec.source];
        this.ring.width = (this.radius + this.ringWidth) * 2;
        this.ring.height = (this.radius + this.ringWidth) * 2;
    }

    public flip(axis: 'x' | 'y') {
        this.token.anchor[axis] = 1 - this.token.anchor[axis];
        this.token.scale[axis] *= -1;
    }

    public recenter() {
        this.viewport.moveCenter(this.center, this.center);
        this.viewport.wheel({ smooth: 3, percent: -0.8, center: this.viewport.center });
    }

    public async import() {
        if (Store.connection.getValue() === false) {
            this.snackbar.open('Cannot upload files while disconnected.', 'Dismiss', { duration: 3000 });
            return;
        }

        const exportMask = this.container.addChild(new Graphics());
        exportMask.circle(this.center, this.center, this.radius).fill({ color: 0xcccccc, alpha: 1 });
        this.token.mask = exportMask;
        this.mask.clear().circle(this.center, this.center, this.radius).cut();

        if (this.alpha == 0) {
            this.ring.removeFromParent();
        }

        this.background.alpha = 0;
        const canvas = this.app.renderer.extract.canvas(this.app.stage) as HTMLCanvasElement;
        canvas.toBlob(async blob => {
            const filename = await Account.uploadImage(blob as Blob);
            const token: IRoomAsset = {
                id: crypto.randomUUID(),
                name: filename,
                uploaderId: Account.id
            }
            Room.tokenAssets.set(token.id, token);

            this.app.destroy();
            this.dialogRef.close();
        });
    }

    public ngOnDestroy() {
        View.viewport.eventMode = 'static';
    }

    public rotate(e: PointerEvent) {
        if (!this.isRotating) return;
        e.preventDefault();
        const rect = this.app.canvas.getBoundingClientRect();
        const mouse = new Point(e.clientX - rect.x, e.clientY - rect.y);
        const center = this.viewport.toScreen(this.token.position.clone());
        const mouseAngle = Util.getRadianAngle(center, mouse);
        this.token.rotation = mouseAngle + this.rotationOffset;
    }

    public beginRotate(e: PointerEvent) {
        if (e.button === 2) {
            e.preventDefault();
            const rect = this.app.canvas.getBoundingClientRect();
            const mouse = new Point(e.clientX - rect.x, e.clientY - rect.y);
            const center = this.viewport.toScreen(this.token.position.clone());
            const mouseAngle = Util.getRadianAngle(center, mouse);
            this.rotationOffset = this.token.rotation - mouseAngle;
            const rotator = document.getElementById('tokenizer-upload') as HTMLElement;
            rotator.style.cursor = 'grabbing';
            this.isRotating = true;
        }
    }

    public endRotate(e: PointerEvent) {
        if (e.button === 2) {
            e.preventDefault();
            const rotator = document.getElementById('tokenizer-upload') as HTMLElement;
            rotator.style.cursor = 'move';
            this.isRotating = false;
        }
    }
}
